import * as React from "react";

import "./color-picker.scss";

const widthGradient = 284
const heightGradient = 213

const widthSlider = widthGradient
const heightSlider = 39

const red = "#FF0000"
const yellow = "#FFFF00"
const green = "#00FF00"
const cyan = "#00FFFF"
const blue = "#0000FF"
const magenta = "#FF00FF"

const colorsRainbowGradient = [red, yellow, green, cyan, blue, magenta]
const radius = 10
const lineWidth = 2
const offset = radius + lineWidth

const coordinatesDefault = {x: widthGradient - offset, y: offset}

export const ColorPicker = ({initialColor, onChange}) => {
    const [colorFinal, setColorFinal] = React.useState(initialColor)

    const gradientRef = React.useRef(null)
    const sliderRef = React.useRef(null)

    const [color, setColor] = React.useState(initialColor)
    const [moveColor, setMoveColor] = React.useState(false)

    const moveColorOn = () => {
        setMoveColor(true)
    }

    const moveColorOff = () => {
        setMoveColor(false)
    }


    const [coordinatesColor, setCoordinatesColor] = React.useState(coordinatesDefault)
    const colorByCoordinates = (e, force = false) => {
        if(moveColor || force) {
            const canvas = gradientRef.current
            const rect = canvas.getBoundingClientRect()
            const context = canvas.getContext('2d')

            let x = e.clientX - rect.left
            if(x < offset) {
                x = offset
            }
            if(x > canvas.width - offset) {
                x = canvas.width - offset
            }

            let y = e.clientY - rect.top
            if(y < offset) {
                y = offset
            }
            if(y > canvas.height - offset) {
                y = canvas.height - offset
            }

            const coordinates = {x: x, y: y}           
            const data = context.getImageData(x, y, 1, 1).data
            const hex = hexFromData(data)

            setColorFinal(hex)
            onChange(hex)
            setCoordinatesColor(coordinates)
            drawCanvasGradient(context, color, coordinates)
        }
    }
    
    const [horizontalColor, setHorizontalColor] = React.useState(offset)
    const colorByHorizontal = (e, force = false) => {
        if(moveColor || force) {
            const canvas = sliderRef.current
            const rect = canvas.getBoundingClientRect()
            const context = canvas.getContext('2d')

            let x = e.clientX - rect.left
            if(x < offset) {
                x = offset
            }
            if(x > canvas.width - offset) {
                x = canvas.width - offset
            }

            const y = e.clientY - rect.top
            const data = context.getImageData(x, y, 1, 1).data           
            const hex = hexFromData(data)

            setColor(hex)
            setColorFinal(hex)
            onChange(hex)
            setHorizontalColor(x)
            setCoordinatesColor(coordinatesDefault)
            drawCanvasLinear(context, x)           
        }
    }

    const clickCoordinates = (e) => {       
        colorByCoordinates(e, true)        
    }

    const clickHorizontal = (e) => {
        colorByHorizontal(e, true)
    }

    React.useEffect(() => {
        const canvas = sliderRef.current
        const context = canvas.getContext('2d')
        
        drawCanvasLinear(context, horizontalColor)

        canvas.addEventListener('pointerdown', moveColorOn)
        const cancelEvents = ['pointercancel', 'pointerout', 'pointerup']
        cancelEvents.forEach((event) =>
            canvas.addEventListener(event, moveColorOff)
        )
        canvas.addEventListener('pointermove', colorByHorizontal)
        canvas.addEventListener('click', clickHorizontal)

        return () => {
            canvas.removeEventListener('pointerdown', moveColorOn)
            cancelEvents.forEach((event) =>
                canvas.removeEventListener(event, moveColorOff)
            )
            canvas.removeEventListener('pointermove', colorByHorizontal)
            canvas.removeEventListener('click', clickHorizontal)
        }
        
    }, [moveColorOn, moveColorOff, colorByHorizontal, clickHorizontal])

    React.useEffect(() => {
        const canvas = gradientRef.current
        const context = canvas.getContext('2d')

        drawCanvasGradient(context, color, coordinatesColor)

        canvas.addEventListener('pointerdown', moveColorOn)
        const cancelEvents = ['pointercancel', 'pointerout', 'pointerup']
        cancelEvents.forEach((event) =>
            canvas.addEventListener(event, moveColorOff)
        )
        canvas.addEventListener('pointermove', colorByCoordinates)
        canvas.addEventListener('click', clickCoordinates)

        return () => {
            canvas.removeEventListener('pointerdown', moveColorOn)
            cancelEvents.forEach((event) =>
                canvas.removeEventListener(event, moveColorOff)
            )
            canvas.removeEventListener('pointermove', colorByCoordinates)
            canvas.removeEventListener('click', clickCoordinates)
        }

    }, [moveColorOn, moveColorOff, colorByCoordinates, clickCoordinates])

    const setColorFinalByInput = (e) => {
        setCoordinatesColor({x: widthGradient - offset, y: offset})
        const hex = e.target.value
        setColorFinal(hex)
        if(isValidColor(hex)) {
            onChange(hex)
            setColor(hex)
            setHorizontalColor(offset)
            setCoordinatesColor(coordinatesDefault)
        }
    }

    return (
        <div className="color-picker-N2O" >
            <canvas className="canvas-gradient" ref={gradientRef} width={widthGradient} height={heightGradient} />
            <canvas className="canvas-slider" ref={sliderRef} width={widthSlider} height={heightSlider} />
            <input type="text" className="color-hex" value={colorFinal || ''} onChange={setColorFinalByInput} />
            {/* <input type="text" className="color-rgb" value={isValidColor(colorFinal) && textRGB(colorFinal).toUpperCase() || ''} /> */}
        </div>
        
    )
}

const drawCanvasGradient = (context, color, coordinates) => {
    const height = context.canvas.height
    const width = context.canvas.width

    context.clearRect(0, 0, width, height)

    context.fillStyle = color
    context.beginPath()
    context.roundRect(0, 0, width, height, radius)
    context.fill()
    context.closePath()

    const gradientBlack = context.createLinearGradient(0, offset, 0, height - offset)
    gradientBlack.addColorStop(0, "transparent")
    gradientBlack.addColorStop(1, "#000000")
    context.fillStyle = gradientBlack
    context.beginPath()
    context.roundRect(0, 0, width, height, radius)
    context.fill()
    context.closePath()

    const gradientWhite = context.createLinearGradient(offset, 0, width - offset, 0)
    gradientWhite.addColorStop(0, "#FFFFFF")
    gradientWhite.addColorStop(1, "transparent")
    context.fillStyle = gradientWhite
    context.beginPath()
    context.roundRect(0, 0, width, height, radius)
    context.fill()
    context.closePath()

    const rgbComponent = Math.round(((255 * coordinates.x / width) + (255 * coordinates.y / height)) / 2)
    context.strokeStyle = `rgb(${rgbComponent}, ${rgbComponent}, ${rgbComponent})`
    context.beginPath()
    context.lineWidth = lineWidth
    context.arc(coordinates.x, coordinates.y, radius, 0, 2 * Math.PI)
    context.stroke()
    context.closePath()
}

const drawCanvasLinear = (context, x) => {
    const width = context.canvas.width
    const height = context.canvas.height

    const rainbowGradient = context.createLinearGradient(radius, 0, width - 2 * radius, 0)
    for(let i = 0; i < colorsRainbowGradient.length; i++) {
        rainbowGradient.addColorStop(i / (colorsRainbowGradient.length - 1), colorsRainbowGradient[i])
    }

    context.fillStyle = rainbowGradient
    context.beginPath()
    context.roundRect(0, 0, width, height, radius)
    context.fill()
    context.closePath()

    const y = height / 2
    const data = context.getImageData(x, y, 1, 1).data
    const styleComponent = 255 - Math.round((data[0] + data[1] + data[2]) / 3) 
    context.strokeStyle = x === offset ? "transparent" : `rgb(${styleComponent}, ${styleComponent}, ${styleComponent}`
    context.beginPath()
    context.lineWidth = lineWidth
    context.arc(x, y, radius, 0, 2 * Math.PI)
    context.stroke()
    context.closePath()
}

const rgbComponentToHex = (component) => {
    const hex = component.toString(16).toUpperCase()
    if(hex.length === 1) {
        return `0${hex}`
    }
    return hex
}

const rgbFromData = (data) => {
    return `rgb(${data[0]}, ${data[1]}, ${data[2]})`
}

const textRGB = (hex) => {
    return rgbFromData(hexToRGBArray(hex))
}

const hexFromData = (data) => {
    const hexRed = rgbComponentToHex(data[0])
    const hexGreen = rgbComponentToHex(data[1])
    const hexBlue = rgbComponentToHex(data[2])

    return `#${hexRed}${hexGreen}${hexBlue}`
}

const hexToRGBArray = (hex) => {
   const sansPound = parseInt(hex.replace('#', ''), 16)
   const red = (sansPound >> 16) & 255
   const green = (sansPound >> 8) & 255
   const blue = sansPound & 255

   return [red, green, blue]
}

const isValidColor = (hex) => {
    if(hex.length < 7) {
        return false
    }
    const sansPound = parseInt(hex.replace('#', ''), 16)
    const red = (sansPound >> 16) & 255
    const green = (sansPound >> 8) & 255
    const blue = sansPound & 255

    if(red < 0 || red > 255) {
        return false
    }

    if(green < 0 || green > 255) {
        return false
    }

    if(blue < 0 || blue > 255) {
        return false
    }
 
    return true
 }